import styled from 'styled-components';
import { SxProps, Theme } from '@mui/material';
import { Flex, FlexColumn, Chip } from '@elementary/common-ui/components';
import {
  mapNameToColor,
  getFirstLettersOfName,
} from '@elementary/common-ui/utils/common';
import { useAuth } from '@frontegg/react';

const Circle = styled.div<{ color?: string; verified?: boolean }>`
  position: relative;
  min-width: 40px;
  min-height: 40px;
  border-radius: 50%;
  text-align: center;
  background: ${({ color, verified, theme }) =>
    verified ? color || '#000' : theme.palette.common.gray};
  border: ${({ verified, theme }) =>
    !verified && `2.5px dashed ${theme.palette.common.gray}`};
`;

const TextInCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 16px;
`;

const Name = styled(Flex)`
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: ${({ theme }) => theme.palette.common.black};
  gap: 8px;
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Email = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  id: string;
  name: string;
  email: string;
  verified?: boolean;
  sx?: SxProps<Theme>;
};

const UserCard = ({ id, name, email, verified = true, sx }: Props) => {
  const { user: currentUser } = useAuth();
  return (
    <Flex
      sx={[
        { alignItems: 'center', gap: 3, height: '48px' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Circle color={mapNameToColor(id)} verified={verified}>
        <TextInCircle>{getFirstLettersOfName(name ?? '')}</TextInCircle>
      </Circle>

      {name !== email ? (
        <FlexColumn sx={{ gap: 0.5, overflow: 'hidden' }}>
          <Name>
            <NameText>{name}</NameText>
            {currentUser?.email === email && (
              <Chip label="YOU" variant="outlined" size="medium" bold />
            )}
            {!verified && (
              <Chip
                label="Pending"
                variant="outlined"
                size="medium"
                customVariant="dashed"
                disabled
                bold
              />
            )}
          </Name>
          {verified && <Email>{email}</Email>}
        </FlexColumn>
      ) : (
        'Invite pending'
      )}
    </Flex>
  );
};

export default UserCard;
